import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { BreadcrumbActionsService, ConfirmDialogComponent } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { StoreAttributeCategoryViewModel, StoreAttributeTypeViewModel } from '../../models/store-attribute.model';
import { StoreAttributeService } from '../../services/store-attribute.service';
import { AddEditAttributeTypeDialogComponent, AddEditAttributeTypeDialogData} from '../../components/store-attribute-types/dialogs/add-edit-attribute-type-dialog/add-edit-attribute-type-dialog.component';
import { AddEditCategoryDialogComponent, AddEditCategoryDialogData} from '../../components/store-attribute-types/dialogs/add-edit-category-dialog/add-edit-category-dialog.component';
import { FilterEnabledChangeEvent, ManageValuesEvent } from '../../components/store-attribute-types/attribute-type-row/attribute-type-row.component';
import { ManageValuesDialogComponent,ManageValuesDialogData} from '../../components/store-attribute-types/dialogs/manage-values-dialog/manage-values-dialog.component';
import { ManageCategoriesDialogComponent, ManageCategoriesDialogData } from '../../components/store-attribute-types/dialogs/manage-categories-dialog/manage-categories-dialog.component';

@Component({
  selector: 'app-store-attribute-types',
  templateUrl: './store-attribute-types.component.html',
  styleUrls: ['./store-attribute-types.component.scss'],
})
export class StoreAttributeTypesComponent implements OnInit, OnDestroy {
  viewModel$ = this.storeAttributeService.viewModel$;
  expandedCategoryIds = new Set<string>();
  filterToggleSavingId: string | null = null;
  private destroy$ = new Subject<void>();
  private latestCategories: StoreAttributeCategoryViewModel[] = [];

  constructor(
    private storeAttributeService: StoreAttributeService,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private breadcrumbActionsService: BreadcrumbActionsService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbActionsService.setActions([
      { id: 'manage-categories', label: 'Reorder Categories', variant: 'stroked', buttonClass: 'outline' },
      { id: 'add-category', label: '+ Add Category', variant: 'stroked', buttonClass: 'outline' },
      { id: 'add-attribute-type', label: '+ Add Attribute Type', variant: 'raised', buttonClass: 'filled' },
    ]);

    this.breadcrumbActionsService.actionClick$
      .pipe(takeUntil(this.destroy$))
      .subscribe((actionId) => {
        if (actionId === 'manage-categories') {
          this.openManageCategoriesDialog();
        } else if (actionId === 'add-category') {
          this.openAddCategoryDialog();
        } else if (actionId === 'add-attribute-type') {
          this.openAddAttributeTypeDialog();
        }
      });

    this.viewModel$.pipe(takeUntil(this.destroy$)).subscribe((viewModel) => {
      this.latestCategories = viewModel.categories;
      if (!this.expandedCategoryIds.size && viewModel.categories.length) {
        this.expandedCategoryIds.add(viewModel.categories[0].id);
      }
    });
  }

  ngOnDestroy(): void {
    this.breadcrumbActionsService.clearActions();
    this.destroy$.next();
    this.destroy$.complete();
  }

  isExpanded(categoryId: string): boolean {
    return this.expandedCategoryIds.has(categoryId);
  }

  toggleCategory(categoryId: string, expanded: boolean): void {
    if (expanded) {
      this.expandedCategoryIds.add(categoryId);
    } else {
      this.expandedCategoryIds.delete(categoryId);
    }
  }

  openAddCategoryDialog(): void {
    this.dialog.open(AddEditCategoryDialogComponent, {
      width: '480px',
      maxWidth: '92vw',
      maxHeight: '95vh',
      panelClass: 'store-attribute-category-dialog',
      disableClose: true,
      data: {
        existingCategories: this.latestCategories,
      } as AddEditCategoryDialogData,
    });
  }

  openAddAttributeTypeDialog(preselectedCategoryId?: string): void {
    if (!this.latestCategories.length) {
      this.matSnackBar.open('Create a category before adding attribute types', 'OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }

    this.dialog.open(AddEditAttributeTypeDialogComponent, {
      width: '520px',
      maxWidth: '92vw',
      maxHeight: '95vh',
      panelClass: 'store-attribute-attribute-type-dialog',
      disableClose: true,
      data: {
        preselectedCategoryId,
        categories: this.latestCategories,
      } as AddEditAttributeTypeDialogData,
    });
  }

  openEditCategoryDialog(category: StoreAttributeCategoryViewModel): void {
    this.dialog.open(AddEditCategoryDialogComponent, {
      width: '480px',
      maxWidth: '92vw',
      maxHeight: '95vh',
      panelClass: 'store-attribute-category-dialog',
      disableClose: true,
      data: {
        category,
        existingCategories: this.latestCategories,
      } as AddEditCategoryDialogData,
    });
  }

  openManageCategoriesDialog(): void {
    if (this.latestCategories.length < 2) {
      this.matSnackBar.open('At least two categories are required to reorder','OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }

    this.dialog.open(ManageCategoriesDialogComponent, {
      width: '520px',
      maxWidth: '92vw',
      maxHeight: '95vh',
      panelClass: 'store-attribute-manage-categories-dialog',
      disableClose: true,
      data: {
        categories: this.latestCategories,
      } as ManageCategoriesDialogData,
    });
  }

  openManageValuesDialog(event: ManageValuesEvent): void {
    this.dialog.open(ManageValuesDialogComponent, {
      width: '560px',
      maxWidth: '92vw',
      maxHeight: '95vh',
      panelClass: 'store-attribute-manage-values-dialog',
      disableClose: true,
      data: {
        attributeType: event.attributeType,
        categoryName: event.categoryName,
      } as ManageValuesDialogData,
    });
  }

  toggleFilterEnabled(event: FilterEnabledChangeEvent): void {
    if (this.filterToggleSavingId) {
      return;
    }

    const { attributeType, enabled } = event;

    this.filterToggleSavingId = attributeType.id;
    this.storeAttributeService.updateDomainFilterProperty(attributeType.id, enabled).subscribe({
      next: () => {
        this.filterToggleSavingId = null;
        this.storeAttributeService.reload();
        this.matSnackBar.open(
          enabled ? 'Attribute enabled in filters' : 'Attribute removed from filters', 'OK', DEFAULT_SNACKBAR_CONFIG);
      },
      error: () => {
        this.filterToggleSavingId = null;
        this.storeAttributeService.reload();
        this.matSnackBar.open('Error updating filter setting', 'OK', DEFAULT_SNACKBAR_CONFIG);
      },
    });
  }

  deleteAttributeType(attributeType: StoreAttributeTypeViewModel): void {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {title: 'Confirm Delete', message: `Do you want to remove "${attributeType.name}"?`},
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        this.storeAttributeService.deleteDomain(attributeType.id).subscribe({
          next: () => {
            this.storeAttributeService.reload();
            this.matSnackBar.open('Attribute type deleted', 'OK',DEFAULT_SNACKBAR_CONFIG);
          },
        });
      }
    });
  }
}
